/**
 * ClaudeCredentialModal — paste-token form for the user's Claude
 * credential, stored in workspace-credentials and read at deploy time
 * by the Managed Apps deploy gate (resolveClaudeCredential in
 * backend/src/handlers/apps.js).
 *
 * Two input choices via a radio:
 *   - OAuth token (preferred — bills against the user's Claude Code
 *     subscription, minted via `claude setup-token`).
 *   - Anthropic API key (`sk-ant-api…` — bills against API credits).
 *
 * Pattern lifted from OpenAIBillingConnectModal: same modal shell,
 * same reset-on-open behavior, same stopPropagation guards. Differences:
 * adds a radio above the input so the user picks which kind. Sends the
 * kind through as the workspace-credentials `type` field (oauth | api).
 *
 * onConnected({ mode, suffix }) — caller uses this to refresh "now using
 * …abc123" affordances inline (Deploy modal State A line, Settings card
 * status, etc).
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function ClaudeCredentialModal({ isOpen, onClose, onConnected, defaultKind = 'oauth' }) {
  const [kind, setKind] = useState(defaultKind);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setKind(defaultKind);
      setToken('');
      setError('');
      setSubmitting(false);
    }
  }, [isOpen, defaultKind]);

  // Best-effort auto-detect on paste/type — flip the radio if the token
  // format clearly matches the OTHER kind. Doesn't override an explicit
  // user pick; just nudges.
  const handleTokenChange = (next) => {
    setToken(next);
    setError('');
    const lower = (next || '').trim();
    if (/^sk-ant-oat/i.test(lower) || /^oat_/i.test(lower)) {
      if (kind !== 'oauth') setKind('oauth');
    } else if (/^sk-ant-api/i.test(lower)) {
      if (kind !== 'api') setKind('api');
    }
  };

  const handleSubmit = async () => {
    const trimmed = token.trim();
    if (!trimmed) {
      setError('Token is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await WorkspaceCredentials.add({ type: kind, token: trimmed });
      const suffix = res?.credential?.masked || '';
      onConnected?.({ mode: kind, suffix });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save Claude credential. Please check the token and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Claude"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Claude</h2>
        <p className="lark-modal-description">
          Zibby needs a Claude credential to run your apps&apos; agent.
          Tokens are KMS-encrypted at rest and only decrypted inside your
          per-instance Fargate task. Pick one:
        </p>

        <div
          className="lark-form-group"
          role="radiogroup"
          aria-label="Claude credential kind"
          style={{ display: 'flex', gap: 12, marginBottom: 14 }}
        >
          <label
            style={{
              flex: 1, cursor: submitting ? 'not-allowed' : 'pointer',
              padding: 10, borderRadius: 6,
              border: `1px solid ${kind === 'oauth' ? 'var(--ion-color-primary)' : 'rgba(255,255,255,0.15)'}`,
              background: kind === 'oauth' ? 'rgba(99,102,241,0.12)' : 'transparent',
            }}
          >
            <input
              type="radio"
              name="claude-cred-kind"
              value="oauth"
              checked={kind === 'oauth'}
              onChange={() => setKind('oauth')}
              disabled={submitting}
              style={{ marginRight: 8 }}
            />
            <span style={{ fontWeight: 600 }}>OAuth (recommended)</span>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
              Mint with <code>claude setup-token</code>. Bills against your
              Claude Code subscription.
            </div>
          </label>
          <label
            style={{
              flex: 1, cursor: submitting ? 'not-allowed' : 'pointer',
              padding: 10, borderRadius: 6,
              border: `1px solid ${kind === 'api' ? 'var(--ion-color-primary)' : 'rgba(255,255,255,0.15)'}`,
              background: kind === 'api' ? 'rgba(99,102,241,0.12)' : 'transparent',
            }}
          >
            <input
              type="radio"
              name="claude-cred-kind"
              value="api"
              checked={kind === 'api'}
              onChange={() => setKind('api')}
              disabled={submitting}
              style={{ marginRight: 8 }}
            />
            <span style={{ fontWeight: 600 }}>API key</span>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
              <code>sk-ant-api…</code> from console.anthropic.com. Bills
              against your Anthropic API credits.
            </div>
          </label>
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="claude-cred-token">
            {kind === 'oauth' ? 'OAuth token' : 'API key'}
          </label>
          <input
            id="claude-cred-token"
            type="password"
            value={token}
            placeholder={kind === 'oauth' ? 'sk-ant-oat01-…' : 'sk-ant-api03-…'}
            onChange={(e) => handleTokenChange(e.target.value || '')}
            className="lark-form-input"
            disabled={submitting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={submitting || !token.trim()}
            aria-label="Save Claude credential"
          >
            {submitting ? <IonSpinner name="crescent" /> : 'Save'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={submitting}
            aria-label="Cancel Claude credential entry"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
